import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AppText, Languages } from '../interfaces/common';
import { PlokkService } from '../services/plokk.service';
import { LayoutService } from '../services/layout.service';

@Component({
  selector: 'app-dash',
  templateUrl: './dash.component.html',
  styleUrls: ['./dash.component.css']
})
export class DashComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() selText: AppText = {};
  @Input() settings: any;
  @Input() lang: Languages = Languages.Icelandic;

  private subAppSettings: any;

  showBanner: boolean = false;
  showPlokkStats: boolean = false;
  bannerFontSize: string = '';
  bannerText: string = '';
  bannerType: 'warning' | 'info' = 'warning';
  bannerDismissed: boolean = false;
  bannerCloseable: boolean = false;

  @Output() bannerDismissedEE = new EventEmitter<void>();

  @ViewChild('bannerWrapper') bannerWrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('bannerMessage') bannerMessageRef!: ElementRef<HTMLElement>;

  constructor(private plokkService: PlokkService, private changeDetector: ChangeDetectorRef, private layoutService: LayoutService) { }

  detailedStats: any = null
  overViewStats: any = { spatial: { 'km': 0 }, activities: { reserved: 0, returned: 0 } }
  dashPrimaryColor: string = '#0b5ea8';

  ngOnInit(): void {
    this.subAppSettings = this.plokkService?.getAppSettings();
    this.bannerText = this.resolveBannerText();
    this.showBanner = this.bannerText.length > 0;
    this.showPlokkStats = !this.showBanner && this.subAppSettings?.subAppName === 'plokk';

    if (this.showPlokkStats && this.subAppSettings?.style?.primaryColor) {
      this.dashPrimaryColor = this.subAppSettings.style.primaryColor;
    }

    if (this.showPlokkStats) this.initializePlokkStats();
  }

  ngAfterViewInit(): void {
    if (this.showBanner) {
      setTimeout(() => this.fitBannerText(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang'] && !changes['lang'].firstChange) {
      this.bannerText = this.resolveBannerText();
      this.showBanner = this.bannerText.length > 0 && !this.bannerDismissed;
      if (this.showBanner) {
        setTimeout(() => this.fitBannerText(), 0);
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.showBanner) {
      this.fitBannerText();
    }
  }

  dismissBanner(): void {
    this.bannerDismissed = true;
    this.showBanner = false;
    this.bannerDismissedEE.emit();
  }

  private resolveBannerText(): string {
    const appLayout = this.layoutService.appLayoutSettings;
    if (!appLayout?.dashBannerType) return '';
    this.bannerType = appLayout.dashBannerType;
    this.bannerCloseable = appLayout.dashCloseable === true;
    const text = this.lang === Languages.Icelandic ? appLayout.dashTextIs : appLayout.dashTextEn;
    return (text ?? '').trim();
  }

  private fitBannerText(): void {
    const wrapper = this.bannerWrapperRef?.nativeElement;
    const message = this.bannerMessageRef?.nativeElement;
    if (!wrapper || !message) return;

    const style = getComputedStyle(wrapper);
    const verticalPadding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const availableHeight = wrapper.clientHeight - verticalPadding;

    const maxFontSize = 15;
    const minFontSize = 6;
    let fontSize = maxFontSize;
    message.style.fontSize = fontSize + 'px';

    while (message.scrollHeight > availableHeight && fontSize > minFontSize) {
      fontSize -= 0.5;
      message.style.fontSize = fontSize + 'px';
    }

    this.bannerFontSize = fontSize + 'px';
    this.changeDetector.detectChanges();
  }

  private initializePlokkStats(): void {
    this.plokkService?.getDetailedStats();
    this.plokkService?.getOverviewStats();

    this.plokkService?.getStatsDetailed().subscribe((stats: any) => {
      this.detailedStats = stats;
      this.detailedStats.urls = stats?.image_url;
      this.detailedStats.notes = stats?.notes;
      this.detailedStats.posts = stats?.totalNumberOfPosts;
      this.detailedStats.trash.kg.total = Math.round(this.detailedStats?.trash?.kg?.total)
      this.changeDetector.detectChanges();
    });

    this.plokkService?.getStatsOverview().subscribe((stats: any) => {
      const totalKm = stats?.spatialData?.[0]?.km;
      const reservedCount = stats?.activities?.[0]?.countreserved;
      const returnedCount = stats?.activities?.[0]?.countreturned;
      this.overViewStats.spatial['km'] = Math.round(totalKm ?? 0);
      this.overViewStats.activities.reserved = reservedCount ?? 0;
      this.overViewStats.activities.returned = returnedCount ?? 0;
      this.changeDetector.detectChanges();
    });
  }
}